import React, { useEffect } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { useToasts } from 'react-toast-notifications';

import { ROUTES } from 'app/utils/constants/paths';
import type { ToastAppearance } from 'ui';

import useIsAIQuotaExhausted from './useIsAIQuotaExhausted';

type AddToast = (content: React.ReactNode, options: { appearance: ToastAppearance; autoDismiss: boolean }) => void;

export function createTeacherToolsQuotaExhausted(): () => boolean {
  let hasToastBeenShown = false;

  return function useTeacherToolsQuotaExhausted(): boolean {
    const { t } = useTranslation(['chatbot']);
    const { addToast }: { addToast: AddToast } = useToasts();
    const isQuotaExhausted = useIsAIQuotaExhausted();

    useEffect(() => {
      if (!isQuotaExhausted || hasToastBeenShown) {
        return;
      }
      hasToastBeenShown = true;
      // The link reuses the History's title so the two cannot drift apart. It does not dismiss itself
      // because it carries a link: with the keyboard there is no time to reach it first (WCAG 2.2.1).
      addToast(
        <Trans
          components={[<Link key="history" to={ROUTES.TEACHER_TOOLS_HISTORY} />]}
          i18nKey="chatbot:quota_teacher_exhausted_toast"
          t={t}
          values={{ history: t('chatbot:teacher_tools_history_title') }}
        />,
        { appearance: 'error', autoDismiss: false }
      );
    }, [addToast, isQuotaExhausted, t]);

    return isQuotaExhausted;
  };
}

/** Only the panel and the tool setup call it: with no interactions left they disable whatever creates content. */
const useTeacherToolsQuotaExhausted = createTeacherToolsQuotaExhausted();

export default useTeacherToolsQuotaExhausted;
